import { DefaultOptionType } from 'antd/lib/select'
import { CnKeywordsData, KeywordGroupWithMembers } from './keywords-interfaces'

// Cache pour les données des mots clés
let cachedData: CnKeywordsData | null = null 

// Promesse pour la récupération des données des mots clés
let fetchPromise: Promise<CnKeywordsData> | null = null

// Fonction pour récupérer les données des mots clés (mots clés, groupes, appartenance à des groupes)
export function fetchKeywordsData(): Promise<CnKeywordsData> {
  if (cachedData) {
    return Promise.resolve(cachedData)
  }

  if (fetchPromise) {
    return fetchPromise
  }

  fetchPromise = fetch('/cnegoces/getCnKeywordsList')
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load keywords: ${response.status}`)
      }
      return response.json() as Promise<CnKeywordsData>
    })
    .then(data => {
      cachedData = data
      return data
    })
    .catch(error => {
      fetchPromise = null
      console.error('Error loading keywords from database:', error)
      throw error
    })

  return fetchPromise
}

// Fonction pour récupérer la liste des mots clés
export function getKeywordsList(): string[] {
  return (cachedData?.keywords ?? [])
    .map(keyword => keyword.keyword)
    .sort((a, b) => a.localeCompare(b))
}

// Fonction pour récupérer les groupes de mots clés avec les mots clés appartenant à chaque groupe
export function getKeywordGroupsWithMembers(): KeywordGroupWithMembers[] {
  if (!cachedData) {
    return []
  }

  const keywordById = new Map(cachedData.keywords.map(keyword => [keyword.id, keyword.keyword]))
  const membersByGroupId = new Map<string, string[]>()

  cachedData.groupMembers.forEach(member => {
    const keyword = keywordById.get(member.keywordId)
    if (!keyword) {
      return
    }

    const groupKeywords = membersByGroupId.get(member.groupId) ?? []
    groupKeywords.push(keyword)
    membersByGroupId.set(member.groupId, groupKeywords)
  })

  return cachedData.groups
    .map(group => ({
      id: group.id,
      name: group.name,
      keywords: (membersByGroupId.get(group.id) ?? []).sort((a, b) => a.localeCompare(b))
    }))
    .filter(group => group.keywords.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
}

// Fonction pour filtrer les mots clés en fonction de la recherche
export function filterKeywords(searchValue: string, keywords: string[] = getKeywordsList()): string[] {
  const normalizedSearch = searchValue.toLowerCase()
  return keywords
    .filter(keyword => keyword.toLowerCase().includes(normalizedSearch))
    .sort((a, b) => a.localeCompare(b))
}

// Fonction pour obtenir les options de selection des mots clés
export function getKeywordSelectOptions(searchValue: string): DefaultOptionType[] {
  return filterKeywords(searchValue).map(keyword => ({ label: keyword, value: keyword }))
}

// Fonction pour fusionner les mots clés distincts
export function mergeDistinctKeywords(currentKeywords: string[], keywordsToAdd: string[]): string[] {
  return Array.from(new Set([...currentKeywords, ...keywordsToAdd]))
}

// Fonction pour vérifier si un groupe de mots clés est entièrement sélectionné
export function isGroupFullySelected(groupKeywords: string[], selectedKeywords: string[]): boolean {
  if (groupKeywords.length === 0) {
    return false
  }

  const selected = new Set(selectedKeywords)
  return groupKeywords.every(keyword => selected.has(keyword))
}

// Fonction pour sélectionner ou déselectionner les mots clés d'un groupe
export function toggleGroupKeywords(currentKeywords: string[], groupKeywords: string[]): string[] {
  if (isGroupFullySelected(groupKeywords, currentKeywords)) {
    const toRemove = new Set(groupKeywords)
    return currentKeywords.filter(keyword => !toRemove.has(keyword))
  }

  return mergeDistinctKeywords(currentKeywords, groupKeywords)
}
